"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import type { ResumeDocument } from "@/types/cv";
import {
  createEmptyResume,
  cleanResume,
  isResumeReady,
} from "@/lib/cv-builder/resume-factory";
import {
  getDraft,
  saveDraft,
  type CvBuilderDraft,
} from "@/lib/cv-builder/draft-store";
import { defaultResumeTemplateId } from "@/lib/cv/template";
import { useCvBuilderPolishStore } from "@/stores/cv-builder-polish-store";
import {
  useSupabaseUser,
  useSupabaseAuthLoading,
} from "@/components/supabase-provider";
import { trackEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { CV_BUILDER_STEPS as STEPS } from "@/components/cv-builder/steps/config";
import { BuilderResult } from "@/components/cv-builder/builder-result";
import { BuilderAuthDialog } from "@/components/cv-builder/builder-auth-dialog";
import { CvBuilderPolishDialog } from "@/components/cv-builder/polish-dialog";
import { withCvBuilderAnalyticsContext } from "@/components/cv-builder/cv-builder-analytics";

interface CvBuilderProps {
  locale: string;
  analyticsContext?: UmamiEventData;
}

export function CvBuilder({ locale, analyticsContext }: CvBuilderProps) {
  const router = useRouter();
  const supabaseUser = useSupabaseUser();
  const isAuthLoading = useSupabaseAuthLoading();
  const isAuthenticated = Boolean(supabaseUser);

  const [resume, setResume] = useState<ResumeDocument>(createEmptyResume);
  const [step, setStep] = useState(0);
  const [view, setView] = useState<"form" | "result">("form");
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const openPolishDialog = useCvBuilderPolishStore((state) => state.openDialog);
  const startedRef = useRef(false);
  const trackBuilderEvent = useCallback(
    (eventName: string, properties?: UmamiEventData) => {
      trackEvent(
        eventName,
        withCvBuilderAnalyticsContext(analyticsContext, properties),
      );
    },
    [analyticsContext],
  );

  // Rehydrate any in-progress / ready draft once on mount.
  useEffect(() => {
    let cancelled = false;
    void getDraft().then((draft) => {
      if (cancelled) return;
      if (draft) {
        setResume(draft.resume);
      }
      setHydrated(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // When a visitor finished the form, was sent to sign-in, then came back
  // authenticated, drop them straight on the result step.
  useEffect(() => {
    if (!hydrated || isAuthLoading || !isAuthenticated) return;
    let cancelled = false;
    void getDraft().then((draft) => {
      if (cancelled) return;
      if (draft?.ready) {
        setResume(draft.resume);
        setView("result");
        trackBuilderEvent("builder_completed", { authed: true, resumed: true });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [hydrated, isAuthLoading, isAuthenticated, trackBuilderEvent]);

  // Persist to IndexedDB (debounced) so building survives refresh and sign-in.
  useEffect(() => {
    if (!hydrated) return;
    const handle = setTimeout(() => {
      const draft: CvBuilderDraft = {
        resume,
        templateId: defaultResumeTemplateId,
        ready: view === "result",
        updatedAt: new Date().toISOString(),
      };
      void saveDraft(draft);
    }, 600);
    return () => clearTimeout(handle);
  }, [resume, view, hydrated]);

  const handleChange = useCallback(
    (next: ResumeDocument) => {
      if (!startedRef.current) {
        startedRef.current = true;
        trackBuilderEvent("builder_started");
      }
      setResume(next);
    },
    [trackBuilderEvent],
  );

  const resultContainerRef = useRef<HTMLDivElement>(null);

  const goToResult = useCallback(async () => {
    const cleaned = cleanResume(resume);
    setResume(cleaned);
    await saveDraft({
      resume: cleaned,
      templateId: defaultResumeTemplateId,
      ready: true,
      updatedAt: new Date().toISOString(),
    });
    // Preview is free; only the download gates to sign-up.
    trackBuilderEvent("builder_completed", { authed: isAuthenticated });
    setView("result");
    // Let React render the result view, then scroll the container into view
    // instead of snapping to the top of the page.
    requestAnimationFrame(() => {
      resultContainerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, [resume, isAuthenticated, trackBuilderEvent]);

  // Download requires an account. Stash the finished draft, open the auth gate,
  // then resume on /dashboard/cv-builder so the draft is saved server-side too.
  const handleLockedDownload = useCallback(async () => {
    await saveDraft({
      resume,
      templateId: defaultResumeTemplateId,
      ready: true,
      updatedAt: new Date().toISOString(),
    });
    trackBuilderEvent("builder_signup_gate");
    setIsAuthDialogOpen(true);
  }, [resume, trackBuilderEvent]);

  const handlePolishedResume = useCallback(async (polished: ResumeDocument) => {
    setResume(polished);
    await saveDraft({
      resume: polished,
      templateId: defaultResumeTemplateId,
      ready: true,
      updatedAt: new Date().toISOString(),
    });
  }, []);

  const handleNext = useCallback(() => {
    trackBuilderEvent("builder_step_completed", { step: STEPS[step].key });
    if (step < STEPS.length - 1) {
      setStep((current) => current + 1);
    } else {
      void goToResult();
    }
  }, [step, goToResult, trackBuilderEvent]);

  if (!hydrated) {
    return (
      <div className="flex min-h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (view === "result") {
    const authRedirect = `/${locale}/dashboard/cv-builder?download=1&source=public-builder`;

    return (
      <div ref={resultContainerRef}>
        <BuilderResult
          resume={resume}
          analyticsContext={analyticsContext}
          locale={locale}
          canDownload={isAuthenticated}
          onLockedDownload={isAuthenticated ? undefined : handleLockedDownload}
          onEdit={() => {
            setView("form");
            setStep(0);
          }}
        />
        <BuilderAuthDialog
          open={isAuthDialogOpen}
          onOpenChange={setIsAuthDialogOpen}
          locale={locale}
          redirectTo={authRedirect}
          analyticsContext={analyticsContext}
        />
        <CvBuilderPolishDialog
          resume={resume}
          analyticsContext={analyticsContext}
          outputLanguage={locale === "id" ? "Indonesian" : "English"}
          onPolished={handlePolishedResume}
        />
      </div>
    );
  }

  const isLastStep = step === STEPS.length - 1;
  const canAdvance = !isLastStep || isResumeReady(resume);
  const Current = STEPS[step].Component;
  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between text-sm">
          <span className="font-semibold">{STEPS[step].title}</span>
          <span className="text-muted-foreground">
            Langkah {step + 1} dari {STEPS.length}
          </span>
        </div>
        <Progress value={progress} className="mt-2 h-2" />
      </div>

      <Current resume={resume} onChange={handleChange} />

      <div className="flex items-center gap-3 pt-2">
        {step > 0 ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setStep((current) => current - 1);
            }}
            className="flex-1 sm:flex-none"
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Kembali
          </Button>
        ) : null}
        <Button
          type="button"
          onClick={handleNext}
          disabled={!canAdvance}
          className="flex-1"
        >
          {isLastStep ? "Lihat CV saya" : "Lanjut"}
          <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      </div>
      {isLastStep && !canAdvance ? (
        <p className="text-center text-xs text-muted-foreground">
          Isi minimal nama dan satu pengalaman atau pendidikan dulu ya.
        </p>
      ) : null}
    </div>
  );
}
